
import Vue from 'vue'
import { TranslateResult } from 'vue-i18n'
import { AxiosResponse } from 'axios'
import AuthApi from '@/services/auth/AuthApi'
import OidcApi from '@/services/auth/OidcApi'
import QRCode from 'qrcode'
import User from '@/models/users/User'
import UserSetting from '@/models/users/UserSetting'

import required from '@/mixins/rules/required'
import updateTheme from '@/mixins/update-theme'
import updateLanguage from '@/mixins/update-language'
import { app } from '@/main'

export default Vue.extend({
  props: ['loggedIn'],

  mixins: [required],

  data: () => ({
    loggingIn: false,
    fetchingUser: false,
    showPassword: false,
    invalidCredentials: false,
    insufficientPermissions: false,
    unexpectedError: null as string | null,
    banned: false,
    credential: '',
    password: '',
    qrLoading: false,
    qrExpired: false,
    qrErrorMsg: '',
    qrImage: '',
    qrPlaceholder: '',
    qrSecondsLeft: 60,
    qrUuid: '',
    qrPollUuid: '',
    qrInterval: 5,
    qrPollTimer: 0,
    claiming: false,
    oidcEnabled: OidcApi.configured(),
  }),

  methods: {
    async loginWithOidc() {
      this.loggingIn = true
      this.unexpectedError = null
      try {
        await OidcApi.start()
      } catch (error) {
        this.loggingIn = false
        this.unexpectedError =
          error instanceof Error ? error.message : String(error)
      }
    },
    async finishOidcLogin() {
      this.loggingIn = true
      this.unexpectedError = null
      try {
        const tokens = await OidcApi.finish()
        this.storeTokens({ data: tokens } as AxiosResponse)
        this.$store.commit('setAuthenticated', true)
        this.fetchingUser = true
        this.credential = OidcApi.usernameFromToken(tokens.access_token)
        await this.fetchUser(this.credential)
      } catch (error) {
        this.unexpectedError =
          error instanceof Error ? error.message : String(error)
      } finally {
        this.loggingIn = false
      }
    },
    async login() {
      this.invalidCredentials = false
      this.insufficientPermissions = false
      this.unexpectedError = null
      this.banned = false

      const formValidation = (
        this.$refs.form as Vue & { validate: () => boolean }
      ).validate()
      if (!formValidation) {
        return
      }

      try {
        this.loggingIn = true
        await AuthApi.login(this.credential, this.password)
          .then(async (resp) => {
            this.storeTokens(resp)
            this.$store.commit('setAuthenticated', true)

            this.fetchingUser = true
            await this.fetchUser(this.credential)
          })
          .catch((error) => {
            if (!error.response) {
              this.unexpectedError = error
            } else {
              if (error.response.status === 401) {
                this.invalidCredentials = true
              } else {
                if (error.response.data && error.response.data.message) {
                  this.unexpectedError = error.response.data.message
                } else {
                  this.unexpectedError = error.response
                }
              }
            }
          })
      } catch (error) {
        console.warn('Login API failed')
      } finally {
        this.loggingIn = false
      }
    },
    async fetchUser(username: string) {
      try {
        await User.api
          .findByUsername(username)
          .then(async (response) => {
            let user = response.data

            if (user.banned) {
              this.banned = true
              return
            }

            const hasPermissions = user.roles.some(function (role: string) {
              return ['admin', 'employee', 'manager'].includes(role)
            })

            if (!hasPermissions) {
              this.insufficientPermissions = true
              return
            }

            user = {
              id: user.id,
              credential: this.credential,
              initials: user.username.substring(0, 2),
              name: user.identity ? user.identity.name : null,
              email: user.email ? user.email.address : null,
              username: user.username,
              pidn: user.identity ? user.identity.pidn : null,
              roles: user.roles,
              avatar: user.avatar,
              keycloak_id: user.keycloak_id,
              settings: await this.fetchUserDashboardSettings(user.id),
            }

            localStorage.setItem('user', JSON.stringify(user))

            let params = {}

            const preferredArena = localStorage.getItem('preferred_arena')

            if (preferredArena) {
              params = {
                arena: preferredArena,
              }
            }

            const isAdminOrManager = user.roles.some((r: string) =>
              ['admin', 'manager'].includes(r),
            )
            updateTheme(user.settings.theme, app)
            updateLanguage(user.settings.language)

            const returnPath = localStorage.getItem('ff_return_path')
            if (returnPath) {
              localStorage.removeItem('ff_return_path')
              await this.$router.push(returnPath).catch(() => undefined)
            } else {
              await this.$router.push({
                name: `navigationDrawer.main.${
                  isAdminOrManager ? 'admin' : 'employee'
                }Dashboard`,
                params: params,
              })
            }
          })
          .catch((error) => {
            if (!error.response) {
              this.unexpectedError = error
            } else {
              if (error.response.data && error.response.data.message) {
                this.unexpectedError = error.response.data.message
              } else {
                this.unexpectedError = error.response
              }
            }
          })
      } catch (e) {
        console.warn(`User API failed.`)
        console.log(e)
        localStorage.clear()
        this.$store.commit('setAuthenticated', false)
      } finally {
        this.fetchingUser = false
      }
    },
    async fetchUserDashboardSettings(id: bigint) {
      const defaultSettings = {
        theme: process.env.VUE_APP_THEME || 'Classic',
        language: process.env.VUE_APP_I18N_LOCALE || 'en',
      }
      try {
        const response = await User.api.getSettingsByNamespace(id, 'dashboard')
        response.data.map((setting: UserSetting) => {
          if (setting.key === 'theme') {
            defaultSettings.theme = setting.value
          }
          if (setting.key === 'language') {
            defaultSettings.language = setting.value
          }
        })
      } catch (e) {
        console.warn(`User Settings API failed.`)
        console.log(e)
      }
      return defaultSettings
    },
    storeTokens(resp: AxiosResponse) {
      const now = new Date()
      const expiryDate = new Date(now.getTime() + resp.data.expires_in * 1000)
      localStorage.setItem('access_token', resp.data.access_token)
      localStorage.setItem('refresh_token', resp.data.refresh_token)
      if (resp.data.id_token) {
        localStorage.setItem('id_token', resp.data.id_token)
      }
      localStorage.setItem('expiry_date', expiryDate.toString())
    },
    async showQrLogin() {
      this.stopQrPolling()
      this.qrExpired = false
      this.qrLoading = true
      this.qrImage = ''
      try {
        const arena = localStorage.getItem('preferred_arena')

        const [resp] = await Promise.all([
          AuthApi.initializeSso({
            arena_id: arena ? Number(arena) : null,
            hostname: 'dashboard',
            client_type: 'dashboard',
          }),
          new Promise((resolve) => setTimeout(resolve, 1200)),
        ])
        this.qrUuid = resp.data.uuid
        this.qrPollUuid = resp.data.poll_uuid
        this.qrInterval = resp.data.interval || 5
        this.qrSecondsLeft = Number(resp.data.expires_in) || 60
        const payload = {
          uuid: this.qrUuid,
          hostname: 'dashboard',
          ...(arena ? { arena_id: Number(arena) } : {}),
        }
        this.qrImage = await QRCode.toDataURL(JSON.stringify(payload), {
          width: 176,
          margin: 2,
        })
        this.startQrPolling()
      } catch {
        this.failQr(this.$t('login.qrError'))
      } finally {
        this.qrLoading = false
      }
    },
    startQrPolling() {
      let sincePoll = 0
      this.qrPollTimer = window.setInterval(async () => {
        this.qrSecondsLeft -= 1
        if (this.qrSecondsLeft <= 0) {
          this.failQr(this.$t('login.qrExpired'))
          return
        }
        sincePoll += 1
        if (sincePoll < this.qrInterval || this.claiming) {
          return
        }
        sincePoll = 0
        this.claiming = true
        try {
          const resp = await AuthApi.claimSso(this.qrPollUuid)
          if (!this.qrPollTimer) {
            return
          }
          this.stopQrPolling()
          await this.onQrAuthSuccess(resp)
        } catch (e: any) {
          const err =
            e && e.response && e.response.data && e.response.data.error
          if (err && err !== 'authorization_pending') {
            this.failQr(this.$t('login.qrError'))
          }
        } finally {
          this.claiming = false
        }
      }, 1000)
    },
    stopQrPolling() {
      if (this.qrPollTimer) {
        window.clearInterval(this.qrPollTimer)
        this.qrPollTimer = 0
      }
    },
    failQr(message: string | TranslateResult) {
      this.stopQrPolling()
      this.qrExpired = true
      this.qrErrorMsg = message.toString()
    },
    async onQrAuthSuccess(resp: AxiosResponse) {
      this.storeTokens(resp)
      this.$store.commit('setAuthenticated', true)
      this.fetchingUser = true
      this.credential = this.usernameFromToken(resp.data.access_token)
      await this.fetchUser(this.credential)
      if (this.banned || this.insufficientPermissions || this.unexpectedError) {
        this.resetQr()
      }
    },
    resetQr() {
      this.stopQrPolling()
      this.claiming = false
      this.qrImage = ''
      this.qrExpired = false
    },
    usernameFromToken(token: string) {
      try {
        const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
        const payload = JSON.parse(
          atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, '=')),
        )
        return payload.preferred_username || payload.username || ''
      } catch {
        return ''
      }
    },
  },
  async mounted() {
    if (this.oidcEnabled && OidcApi.callbackPending()) {
      await this.finishOidcLogin()
      return
    }
    const payload = {
      uuid: '00000000-0000-0000-0000-000000000000',
      hostname: 'dashboard',
      arena_id: 1,
    }
    this.qrPlaceholder = await QRCode.toDataURL(JSON.stringify(payload), {
      width: 176,
      margin: 2,
    })
  },
  beforeDestroy() {
    this.stopQrPolling()
  },
})
